/**
 * Boswell DCA challenger vs ARIES exponential forecast.
 * Fits a simple Arps exponential to history and builds alternate qi paths
 * so the desk can price acid-uplift durability risk.
 *
 * Hardening (SPEC-54): optional Arps hyperbolic fit (b capped), holdout error
 * when history allows, data-anchored fade window, and production diagnostics.
 * The single Boswell volume judgment stays `boswellVolAdj` — every scenario's
 * `volAdjVsAries` maps a raw challenger/ARIES volume ratio onto that one dial,
 * with `cfScale` applied exactly once downstream in evaluateDeal.
 */
import { buildProductionDiagnostics } from './productionDiagnostics.js';

/** Documented Arps hyperbolic exponent cap unless a memo justifies otherwise. */
export const HYPERBOLIC_B_MAX = 1.5;

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

/** Ordinary least-squares fit: ln(q) = ln(qi) − D·t  (t in years). */
export function fitExponential(points) {
  const pts = (points || []).filter((p) => p.q > 0 && Number.isFinite(p.t) && Number.isFinite(p.q));
  if (pts.length < 3) return null;
  const n = pts.length;
  let sumT = 0;
  let sumY = 0;
  let sumTT = 0;
  let sumTY = 0;
  for (const p of pts) {
    const y = Math.log(p.q);
    sumT += p.t;
    sumY += y;
    sumTT += p.t * p.t;
    sumTY += p.t * y;
  }
  const den = n * sumTT - sumT * sumT;
  if (Math.abs(den) < 1e-12) return null;
  const slope = (n * sumTY - sumT * sumY) / den; // = −D
  const intercept = (sumY - slope * sumT) / n; // = ln(qi)
  const D = -slope;
  if (!(D > 0) || !Number.isFinite(intercept)) return null;
  const qi = Math.exp(intercept);
  // R² on log space
  const yMean = sumY / n;
  let ssTot = 0;
  let ssRes = 0;
  for (const p of pts) {
    const y = Math.log(p.q);
    const yHat = intercept + slope * p.t;
    ssTot += (y - yMean) ** 2;
    ssRes += (y - yHat) ** 2;
  }
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;
  return {
    qi: +qi.toFixed(1),
    Dannual: +D.toFixed(4),
    DePct: +(D * 100).toFixed(2),
    r2: +r2.toFixed(3),
    n,
  };
}

/** Effective annual decline from continuous D: De = 1 − e^(−D). */
export function effectiveDecline(Dannual) {
  if (Dannual == null || !(Dannual > 0)) return null;
  return 1 - Math.exp(-Dannual);
}

/**
 * Arps hyperbolic fit q(t) = qi / (1 + b·Di·t)^(1/b), t in years.
 * Grid-searches b in (0, bMax] and, for each b, linearizes q^(−b) = A + B·t
 * (OLS), then keeps the b with the lowest rate-space SSE. `b` is capped at
 * `bMax` (default 1.5) per DEAL_INTELLIGENCE §12 — document any override.
 */
export function fitHyperbolic(points, { bMax = HYPERBOLIC_B_MAX } = {}) {
  const pts = (points || []).filter((p) => p.q > 0 && Number.isFinite(p.t) && Number.isFinite(p.q));
  if (pts.length < 4) return null; // need more support than the exponential
  const n = pts.length;
  const qMean = pts.reduce((s, p) => s + p.q, 0) / n;
  let sst = 0;
  for (const p of pts) sst += (p.q - qMean) ** 2;
  let best = null;
  for (let b = 0.05; b <= bMax + 1e-9; b += 0.05) {
    let sT = 0;
    let sY = 0;
    let sTT = 0;
    let sTY = 0;
    for (const p of pts) {
      const y = Math.pow(p.q, -b);
      sT += p.t;
      sY += y;
      sTT += p.t * p.t;
      sTY += p.t * y;
    }
    const den = n * sTT - sT * sT;
    if (Math.abs(den) < 1e-12) continue;
    const B = (n * sTY - sT * sY) / den;
    const A = (sY - B * sT) / n;
    if (!(A > 0) || !(B > 0)) continue; // must be a positive, declining path
    const qi = Math.pow(A, -1 / b);
    const Di = B / (A * b); // nominal initial decline (per year)
    if (!Number.isFinite(qi) || !(Di > 0)) continue;
    let sse = 0;
    for (const p of pts) {
      const qh = qi / Math.pow(1 + b * Di * p.t, 1 / b);
      sse += (p.q - qh) ** 2;
    }
    if (!best || sse < best.sse) {
      best = {
        b: +b.toFixed(2),
        qi: +qi.toFixed(1),
        Di: +Di.toFixed(4),
        DiPct: +(Di * 100).toFixed(2),
        r2: sst > 0 ? +(1 - sse / sst).toFixed(3) : null,
        n,
        sse,
      };
    }
  }
  if (!best) return null;
  const { sse: _sse, ...rest } = best;
  return rest;
}

/**
 * Holdout error for an exponential fitter: fit on all but the last `holdout`
 * points, then report mean absolute % error on the held-out tail. Null when
 * there isn't enough history to hold anything out honestly.
 */
export function holdoutMape(points, holdout = 3) {
  const pts = (points || [])
    .filter((p) => p.q > 0 && Number.isFinite(p.t) && Number.isFinite(p.q))
    .sort((a, b) => a.t - b.t);
  if (pts.length < holdout + 3) return null;
  const train = pts.slice(0, pts.length - holdout);
  const test = pts.slice(pts.length - holdout);
  const fit = fitExponential(train);
  if (!fit) return null;
  const D = -Math.log(1 - effectiveDecline(fit.Dannual)); // == fit.Dannual, kept explicit
  let err = 0;
  let count = 0;
  for (const p of test) {
    const qh = fit.qi * Math.exp(-D * p.t);
    if (qh > 0) {
      err += Math.abs(qh - p.q) / p.q;
      count += 1;
    }
  }
  return count ? +((err / count) * 100).toFixed(1) : null;
}

/** Forward monthly Mcf/d series from qi + continuous annual D. */
export function forecastMonthly({ qi, Dannual, startDate = '2026-06', months = 60 }) {
  const [ys, ms] = startDate.split('-').map(Number);
  const out = [];
  for (let i = 0; i < months; i += 1) {
    const tYears = i / 12;
    const d = new Date(Date.UTC(ys, ms - 1 + i, 1));
    const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const q = qi * Math.exp(-Dannual * tYears);
    out.push({ month: ym, mcfpd: +q.toFixed(1) });
  }
  return out;
}

/**
 * Fade from qiStart to qiEnd over fadeMonths, then exponential at Dannual from qiEnd.
 */
export function forecastFadeThenExp({
  qiStart,
  qiEnd,
  fadeMonths = 6,
  Dannual,
  startDate = '2026-06',
  months = 60,
}) {
  const [ys, ms] = startDate.split('-').map(Number);
  const out = [];
  for (let i = 0; i < months; i += 1) {
    const d = new Date(Date.UTC(ys, ms - 1 + i, 1));
    const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    let q;
    if (i < fadeMonths) {
      const w = fadeMonths <= 1 ? 1 : i / (fadeMonths - 1);
      q = qiStart + (qiEnd - qiStart) * w;
    } else {
      const tYears = (i - fadeMonths) / 12;
      q = qiEnd * Math.exp(-Dannual * tYears);
    }
    out.push({ month: ym, mcfpd: +q.toFixed(1) });
  }
  return out;
}

export function cumMcf(series, months) {
  const slice = (series || []).slice(0, months);
  // Mcf/d × days/mo ≈ Mcf/d × 30.4
  return +slice.reduce((s, r) => s + r.mcfpd * 30.4, 0).toFixed(0);
}

function historyRate(row) {
  if (!row) return NaN;
  const v = row.mcfpd ?? row.bblpd ?? row.bopd ?? row.rate;
  return Number(v);
}

/**
 * Build challenger pack from DealPackage actuals + the seller forecast row.
 * Windows and the starting rate come from the well's history, not from an
 * Eagle gas default. `rateUnit` is Mcf/d for gas and bbl/d for oil.
 * @returns {object|null}
 */
export function buildBoswellDcaChallenger({
  actuals,
  ariesQi = null,
  ariesDeAnnual = 0.06,
  startDate = null,
  useHyperbolic = false,
  rateUnit = 'Mcf/d',
}) {
  if (!actuals) return null;
  const annual = actuals.annual || [];
  const recent = actuals.recentMonths || [];
  const stats = actuals.stats || {};
  const recentRates = recent.map(historyRate).filter((n) => Number.isFinite(n) && n > 0);
  const annualRates = annual.map(historyRate).filter((n) => Number.isFinite(n) && n > 0);
  const sellerQi =
    (Number.isFinite(Number(ariesQi)) && Number(ariesQi) > 0 ? Number(ariesQi) : null) ??
    stats.latestMcfpd ??
    stats.latestBopd ??
    (recentRates.length ? recentRates[recentRates.length - 1] : null) ??
    (annualRates.length ? annualRates[annualRates.length - 1] : null);
  if (!sellerQi) return null;
  const resolvedStart = String(startDate || actuals.lifeOfWell?.lastProdDate || '2026-06').slice(0, 7);
  const trough = stats.troughMcfpd ?? stats.troughBopd ?? (recentRates.length ? Math.min(...recentRates) : sellerQi);
  const postAcid =
    stats.acid?.last30AvgMcfpd ??
    stats.peakRecentMcfpd ??
    stats.peakRecentBopd ??
    (recentRates.length ? Math.max(...recentRates) : sellerQi);

  const years = annual
    .filter((row) => Number(row.year) > 1900 && historyRate(row) > 0)
    .map((row) => Number(row.year));
  const lastYear = years.length ? Math.max(...years) : 2024;
  const wideStart = lastYear - 9;
  const recentStart = lastYear - 4;
  const inWindow = (r, lo, hi) => {
    const y = Number(r.year);
    return y >= lo && y <= hi && historyRate(r) > 0;
  };

  // Wide annual fit: last 10 history years (2015–2024 when that is the history).
  const fitPts = annual
    .filter((r) => inWindow(r, wideStart, lastYear))
    .map((r) => ({ t: Number(r.year) - wideStart, q: historyRate(r) }));
  const firstWide = annual.find((r) => Number(r.year) === wideStart);
  const fit = fitExponential(fitPts) || {
    qi: firstWide ? historyRate(firstWide) : sellerQi,
    Dannual: 0.07,
    DePct: 7,
    r2: null,
    n: fitPts.length,
  };

  const fitRecentPts = annual
    .filter((r) => inWindow(r, recentStart, lastYear))
    .map((r) => ({ t: Number(r.year) - recentStart, q: historyRate(r) }));
  const fitRecent = fitExponential(fitRecentPts);

  // Prefer a monthly fit over annual-only when the monthly series is long
  // enough and pre-acid (reviews: don't lean on annual-only 2020–24). We fit on
  // months BEFORE the observed post-acid peak so the acid spike doesn't flatten
  // the decline. Falls back to annual when monthly is too short/spiky.
  const monthlyPts = recent
    .map((r, i) => ({ i, month: r.month, q: historyRate(r) }))
    .filter((r) => Number.isFinite(r.q) && r.q > 0);
  let peakIdx = -1;
  let peakVal = -Infinity;
  monthlyPts.forEach((r) => {
    if (r.q > peakVal) {
      peakVal = r.q;
      peakIdx = r.i;
    }
  });
  const preAcidMonthly = monthlyPts.filter((r) => (peakIdx >= 0 ? r.i < peakIdx : true));
  const fitMonthlyPts = (preAcidMonthly.length >= 6 ? preAcidMonthly : []).map((r, k) => ({
    t: k / 12,
    q: r.q,
  }));
  const fitMonthly = fitExponential(fitMonthlyPts);

  // Decline used for challenger forecasts, in priority order.
  const D = fitMonthly?.Dannual ?? fitRecent?.Dannual ?? fit.Dannual;
  const wideLabel = wideStart === 2015 && lastYear === 2024 ? 'annual-2015-2024' : `annual-${wideStart}-${lastYear}`;
  const recentLabel = recentStart === 2020 && lastYear === 2024 ? 'annual-2020-2024' : `annual-${recentStart}-${lastYear}`;
  const fitSource = fitMonthly ? 'monthly' : fitRecent ? recentLabel : wideLabel;

  // Optional Arps hyperbolic candidate on the same annual support (evidence
  // only — surfaced for the desk; forecasts still bridge via boswellVolAdj).
  const hyperbolicPts = fitRecentPts.length >= 4 ? fitRecentPts : fitPts;
  const hyperbolic = useHyperbolic || hyperbolicPts.length >= 4 ? fitHyperbolic(hyperbolicPts) : null;

  // Holdout validation when the annual history is long enough to hold a tail.
  const holdoutMapePct = holdoutMape(fitPts.length >= fitRecentPts.length ? fitPts : fitRecentPts);

  // Production diagnostics (uptime / FTP / WGR / acid / reconciliation).
  const diagnostics = buildProductionDiagnostics({ actuals });

  const ariesD = -Math.log(1 - ariesDeAnnual); // convert effective → continuous

  // Data-anchored fade window: use the observed peak→trough month count when the
  // monthly series shows one, otherwise the 6-month default guess.
  const oilRates = /bbl/i.test(String(rateUnit));
  const observedFadeMonths = diagnostics?.acid?.observedFadeMonths ?? null;
  const fadeMonths = observedFadeMonths && observedFadeMonths > 0 ? observedFadeMonths : 6;
  const fadeSource = observedFadeMonths && observedFadeMonths > 0
    ? (oilRates ? 'observed peak-to-trough' : 'observed post-acid')
    : 'default 6-mo guess';
  const holdLabel = oilRates ? 'Hold recent rate' : 'Hold post-acid';

  const scenarios = [
    {
      id: 'aries',
      label: 'Seller forecast',
      class: 'Fact / model',
      qi: sellerQi,
      Dannual: ariesD,
      note: `qi ${sellerQi.toLocaleString()} ${rateUnit} · De ${(ariesDeAnnual * 100).toFixed(0)}%/yr EXP · seller's forecast case`,
      series: forecastMonthly({ qi: sellerQi, Dannual: ariesD, startDate: resolvedStart, months: 60 }),
    },
    {
      id: 'holdAcid',
      label: holdLabel,
      class: 'Judgment · bull',
      qi: postAcid,
      Dannual: D,
      note: `Start at last-30 avg ${Math.round(postAcid)} ${rateUnit} · decline at fitted ${((1 - Math.exp(-D)) * 100).toFixed(1)}% De`,
      series: forecastMonthly({ qi: postAcid, Dannual: D, startDate: resolvedStart, months: 60 }),
    },
    {
      id: 'fadeTrough',
      label: `Fade to trough (${fadeMonths} mo)`,
      class: 'Judgment · base risk',
      qi: postAcid,
      Dannual: D,
      note: oilRates
        ? `Recent rate fades ${Math.round(postAcid)} → ${Math.round(trough)} ${rateUnit} over ${fadeMonths} months (${fadeSource}), then fitted decline`
        : `Acid fades ${Math.round(postAcid)} → ${Math.round(trough)} over ${fadeMonths} months (${fadeSource}), then fitted decline`,
      series: forecastFadeThenExp({
        qiStart: postAcid,
        qiEnd: trough,
        fadeMonths,
        Dannual: D,
        startDate: resolvedStart,
        months: 60,
      }),
    },
    {
      id: 'troughStart',
      label: 'Trough from day 1',
      class: 'Judgment · bear',
      qi: trough,
      Dannual: D,
      note: `Ignore the uplift; start at the observed trough ${Math.round(trough)} ${rateUnit}`,
      series: forecastMonthly({ qi: trough, Dannual: D, startDate: resolvedStart, months: 60 }),
    },
  ];

  for (const s of scenarios) {
    s.cum12Mmcf = +(cumMcf(s.series, 12) / 1000).toFixed(1);
    s.cum60Mmcf = +(cumMcf(s.series, 60) / 1000).toFixed(1);
    // Volume scale vs ARIES for DealPackage boswellVolAdj knob
    s.volAdjVsAries = +clamp(s.qi / sellerQi - 1, -0.75, 0.5).toFixed(3);
    // For fade, use ratio of 12-mo cum vs ARIES 12-mo cum (better than qi)
    if (s.id === 'fadeTrough') {
      const ariesCum12 = cumMcf(scenarios[0].series, 12);
      s.volAdjVsAries = +clamp(cumMcf(s.series, 12) / ariesCum12 - 1, -0.75, 0.5).toFixed(3);
    }
  }

  // Chart overlay: history recent + first 18 mo of each forecast
  const hist = recent.map((r) => ({
    month: r.month,
    actual: historyRate(r),
  }));
  const forecastMonths = scenarios[0].series.slice(0, 18).map((r) => r.month);
  const overlay = [
    ...hist,
    ...forecastMonths.map((month) => {
      const row = { month, actual: null };
      for (const s of scenarios) {
        row[s.id] = s.series.find((x) => x.month === month)?.mcfpd ?? null;
      }
      return row;
    }),
  ];

  const shortWindow = diagnostics?.acid?.shortWindow ?? null;
  const durabilityWarning = shortWindow
    ? oilRates
      ? `Observed high-rate window is ${diagnostics?.acid?.postAcidDays ?? '<90'} days — a short window alone cannot prove that rate holds. Treat "${holdLabel}" as a bull judgment, not evidence.`
      : `Observed post-acid window is ${diagnostics?.acid?.postAcidDays ?? '<90'} days — a short window alone cannot prove the acid uplift is durable. Treat "Hold post-acid" as a bull judgment, not evidence.`
    : null;

  return {
    ariesQi: sellerQi,
    rateUnit,
    fitWindow: { wideStart, recentStart, lastYear },
    ariesDeAnnual,
    troughMcfpd: +Number(trough).toFixed(1),
    postAcidMcfpd: +Number(postAcid).toFixed(1),
    fit2015_2024: fit,
    fit2020_2024: fitRecent,
    fitMonthly,
    fitSource,
    hyperbolic,
    holdoutMapePct,
    fadeMonths,
    fadeSource,
    diagnostics,
    fitReady: diagnostics?.fitReady ?? null,
    fitReadyReasons: diagnostics?.fitReadyReasons ?? [],
    shortWindow,
    durabilityWarning,
    bMax: HYPERBOLIC_B_MAX,
    Dused: +D.toFixed(4),
    DeUsedPct: +((1 - Math.exp(-D)) * 100).toFixed(2),
    scenarios: scenarios.map(({ series, ...rest }) => ({
      ...rest,
      series18: series.slice(0, 18),
    })),
    overlay,
    verdict:
      postAcid > sellerQi * 0.95 && trough < sellerQi * 0.75
        ? 'The seller forecast case capitalizes the recent high rate. If rates revert toward the observed trough, package PV is materially overstated — use Fade / Trough cases on the bid desk.'
        : "Compare the seller's forecast case to recent actuals before locking a bid.",
  };
}

/** Map a DCA scenario id → boswellVolAdj for evaluateDeal. */
export function volAdjForScenario(challenger, scenarioId) {
  const s = challenger?.scenarios?.find((x) => x.id === scenarioId);
  return s?.volAdjVsAries ?? 0;
}
